import {
    CSSProperties,
    ElementRef,
    JSX,
    ReactNode,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';
import classNames from 'classnames';
import { IImageConfigValue, ImageFormat, IXor } from '@bg-shared';
import BounceLoader from 'react-spinners/BounceLoader';
import { buildUrl } from './utils/buildUrl';
import { getSourceMediaQuery } from './utils/getSourceMediaQuery';
import classes from './Image.module.scss';

interface IImageConfigProps extends IImageConfigValue {
    filename: string;
}

interface IImageSrcProps {
    src: string;
}

interface IProps {
    alt: string;
    showLoader?: boolean;
    lazyLoad?: boolean;
    onLoad?(): void;
    className?: string;
    classNameContainer?: string;
    fit?: 'cover' | 'contain';
    style?: Pick<CSSProperties, 'objectPosition'>;
    branding?: string;
    dataQA?: string;
    useDefaultSrc?: boolean;
    onError?(): void;
}

const IMAGE_FORMAT_TYPE: Record<ImageFormat, string> = {
    [ImageFormat.AVIF]: 'image/avif',
    [ImageFormat.WEBP]: 'image/webp',
    [ImageFormat.JPG]: 'image/jpeg',
    [ImageFormat.PNG]: 'image/png',
};

const defaultImageFormats = [ImageFormat.JPG];

export const Image = ({
    formats = defaultImageFormats,
    fit = 'cover',
    ...props
}: IProps & IXor<IImageConfigProps, IImageSrcProps>): JSX.Element => {
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const imgRef = useRef<ElementRef<'img'>>(null);

    const handleImageLoad = () => {
        setIsLoading(false);
        props.onLoad?.();
    };

    const handleError = () => {
        setIsLoading(false);
        props.onError?.();
    };

    const buildSources = (): ReactNode => {
        if (props.sizes) {
            return props.sizes.map((size, index) => {
                const media =
                    props.media?.(props.sizes, index) ?? getSourceMediaQuery(props.sizes, index);

                return formats.map((format) => (
                    <source
                        key={`${size}-${format}`}
                        srcSet={buildUrl(props.path, props.filename, size, format, props.branding)}
                        type={IMAGE_FORMAT_TYPE[format]}
                        media={media}
                    />
                ));
            });
        }

        if (!props.useDefaultSrc) {
            return formats.map((format) => (
                <source
                    key={format}
                    srcSet={`${props.src}.${format}`}
                    type={IMAGE_FORMAT_TYPE[format]}
                />
            ));
        }

        return <source srcSet={props.src} />;
    };

    useLayoutEffect(() => {
        // need to set src after components are rendered,
        // otherwise <img> is loaded as standalone element and on Safari both images are fetched from <source> and <img>
        if (props.src && !props.sizes) {
            imgRef.current.src = props.src;
        } else {
            const initialFormat = formats?.[formats.length - 1] ?? ImageFormat.JPG;

            imgRef.current.src = props.sizes
                ? buildUrl(
                      props.path,
                      props.filename,
                      props.sizes[props.sizes.length - 1],
                      initialFormat,
                      props.branding,
                  )
                : `${props.src}.${initialFormat}`;
        }
    }, [props.src]);

    const sources = buildSources();

    return (
        <div
            className={classNames(classes.container, props.classNameContainer)}
            data-qa={props.dataQA}
        >
            {isLoading && (
                <>
                    <div className={classes.placeholder} />
                    {props.showLoader && (
                        <BounceLoader
                            className={classes.loader}
                            loading
                            color="var(--primary)"
                            size={30}
                        />
                    )}
                </>
            )}
            <picture className={classes.picture}>
                {sources}
                <img
                    ref={imgRef}
                    src=""
                    alt={props.alt}
                    loading={props.lazyLoad ? 'lazy' : 'eager'}
                    onLoad={handleImageLoad}
                    onError={handleError}
                    className={classNames(classes.image, props.className, classes[fit], {
                        [classes.loading]: isLoading,
                    })}
                    style={props.style}
                />
            </picture>
        </div>
    );
};
